import { ArrowTopRightOnSquareIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { Button, Card, InfoBox, Property } from "../../ui";
import { countryName, formatDuration } from "../model";
import { fileSummary, formatDateTime, openDocument, sectorLabel } from "./model";
import { AMOUNTS, formatMoney } from "../funding";

const yesNo = (value) => (value == null ? "—" : value ? "Yes" : "No");

// The abroad form's answers on an application carried over from the abroad scholarship records
const ABROAD_FORM_ANSWERS = [
  ["status", "Status in the abroad records"],
  ["previousStudyLevel", "Previous level"],
  ["newStudyLevel", "Level applied for"],
  ["academicPerformance", "Academic performance"],
  ["academicMerit", "Academic merit"],
  ["completionYear", "Completion year"],
  ["countryOfStudy", "Country of study"],
  ["universityAppliedFor", "University applied for"],
  ["courseAppliedFor", "Course applied for"],
];

// One file: Open shows it in the document viewer (PDFs and images inline); `actions` sit after Open
export const DocumentRow = ({ title, detail, onOpen, actions }) => (
  <li className="flex items-center justify-between gap-3 py-2">
    <div className="flex min-w-0 items-center gap-3">
      <DocumentTextIcon className="h-5 w-5 shrink-0 text-brand" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800">{title}</p>
        {detail && <p className="truncate text-xs text-slate-500">{detail}</p>}
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-1">
      <Button size="sm" variant="outline" icon={<ArrowTopRightOnSquareIcon className="h-4 w-4" />} onClick={onOpen}>
        Open
      </Button>
      {actions}
    </div>
  </li>
);

/**
 * What the student sent: their studies as at submission, the programme, the form and the
 * documents. `documentPath(id)` is the download-url path for the viewer's role, and
 * `attachmentPath(id)` the one for files attached in the abroad scholarship records (HEC and evaluators only).
 */
export default function ApplicationContents({ application, documentPath, attachmentPath }) {
  const { profile, programme, form, documents = [], attachments = [] } = application;
  const funding = form?.funding;
  const abroadForm = profile?.source === "ABROAD_FORM";
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card title="Education" subtitle="As the student gave it when applying">
        {profile?.unmetRules?.length > 0 && (
          <InfoBox className="mb-3">
            <p className="font-medium">Does not meet who may apply:</p>
            <ul className="list-disc pl-5">
              {profile.unmetRules.map((r) => (
                <li key={`${r.kind}:${r.value}`}>{r.label}</li>
              ))}
            </ul>
          </InfoBox>
        )}
        <Property label="Current level" value={profile?.currentLevel?.name} />
        <Property label="Field of study" value={profile?.fieldOfStudy?.name} />
        <Property label="School or university" value={profile?.institutionName} />
        <Property label="Programme or combination" value={profile?.programmeName} />
        <Property label="Final grade" value={profile?.gradePercent == null ? null : `${profile.gradePercent}%`} />
        <Property label="Completed in" value={profile?.completionYear} />
        {abroadForm && ABROAD_FORM_ANSWERS.map(([key, label]) => <Property key={key} label={label} value={profile.abroadForm?.[key] || null} />)}
      </Card>
      {/* On a loan the student names where they were admitted; HEC's programme is then the study level */}
      <Card title="Programme applied for">
        <Property label="Programme" value={programme?.name} />
        {form?.admittedProgramme && <Property label="Course admitted to" value={form.admittedProgramme} />}
        <Property label="University" value={form?.admittedInstitution ?? programme?.institutionName} />
        <Property label="Country" value={countryName(form?.studyCountryCode ?? programme?.countryCode)} />
        <Property label="Level" value={programme?.level?.name} />
        <Property label="Duration" value={formatDuration(programme?.durationMonths)} />
      </Card>
      <Card title="Application form">
        {form?.courseSector && <Property label="Priority sector" value={sectorLabel(form.courseSector)} />}
        <Property label="Currently employed" value={yesNo(form?.employed)} />
        {form?.employed && <Property label="Employer" value={form.employer} />}
        {"consent" in application && (
          <Property label="Declaration" value={application.consent ? `Agreed ${formatDateTime(application.consent.givenAt)}` : "Not recorded (carried over from the abroad records)"} />
        )}
      </Card>
      {funding?.currency && (
        <Card title="Funding requested">
          {AMOUNTS.map(({ key, label }) => (
            <Property key={key} label={label} value={formatMoney(funding[key], funding.currency)} />
          ))}
          <Property label="Programme dates" value={`${funding.studyStartDate ?? "—"} to ${funding.studyEndDate ?? "—"}`} />
          {funding.coveredPeriodMonths != null && <Property label="Months covered / remaining" value={`${funding.coveredPeriodMonths} / ${funding.remainingPeriodMonths ?? "—"}`} />}
        </Card>
      )}
      <Card title={`Documents (${documents.length + attachments.length})`}>
        {documents.length + attachments.length === 0 ? (
          <p className="text-sm text-slate-500">No documents.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-slate-100">
            {documents.map((d) => (
              <DocumentRow
                key={d.id}
                title={d.requirementLabel ?? "Document"}
                detail={`${fileSummary(d)} · ${formatDateTime(d.uploadedAt)}`}
                onOpen={() => openDocument(documentPath(d.id), d.fileName)}
              />
            ))}
          </ul>
        )}
        {attachments.length > 0 && attachmentPath && (
          <>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Attached in the abroad records</p>
            <ul className="flex flex-col divide-y divide-slate-100">
              {attachments.map((d) => (
                <DocumentRow key={d.id} title={d.fileName} onOpen={() => openDocument(attachmentPath(d.id), d.fileName)} />
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}
